import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectAuth, fetchUserDetails } from "../../../store/auth_slice";
import Button from "../../button/button";
import { AppDispatch } from "../../../store/store";
import apiClient from "../../../services/api_client";

interface AccountDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

const AccountDetailsCard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(selectAuth);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [details, setDetails] = React.useState<AccountDetails>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
  });

  React.useEffect(() => {
    if (user) {
      setDetails({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber || "",
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Send update to backend
      await apiClient.put('/users/me', details);
      
      // Fetch updated user details to ensure store is in sync
      dispatch(fetchUserDetails());
      
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update details');
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = (key: keyof AccountDetails, label: string) => (
    <div>
      <p className="font-semibold">{label}</p>
      {isEditing ? (
        <input
          type="text"
          name={key}
          value={details[key]}
          onChange={handleInputChange}
          className="w-full p-1 border rounded"
          disabled={isLoading}
        />
      ) : (
        <p>{details[key]}</p>
      )}
    </div>
  );

  if (!user) {
    return <div>Please log in to view account details.</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="border-b px-4 py-2">
        <h3 className="text-lg font-semibold">Account details</h3>
      </div>
      <div className="grid grid-cols-2 gap-4 px-4 mt-4">
        {renderField("firstName", "First Name")}
        {renderField("lastName", "Last Name")}
        {renderField("email", "Email")}
        {renderField("phoneNumber", "Phone")}
      </div>
      {error && (
        <div className="px-4 mt-2 text-red-600">
          {error}
        </div>
      )}
      <div className="p-4">
        {isEditing ? (
          <Button 
            buttonType="primary" 
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save details"}
          </Button>
        ) : (
          <Button buttonType="primary" onClick={handleUpdate}>
            Update details
          </Button>
        )}
      </div>
    </div>
  );
};

export default AccountDetailsCard;
